import React, { FC, useEffect, useState } from 'react'
import { isBrowser } from 'src/helpers/layout'

interface ISideNavigationProps {
  className?: string
}

const SideNavigation: FC<ISideNavigationProps> = ({ className }) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [sections, setSections] = useState<HTMLElement[]>([])
  const [isClickScrolling, setIsClickScrolling] = useState(false)

  const getSections = (): HTMLElement[] =>
    Array.from(document.querySelectorAll('section[data-name]:not(#navigable-page-hero), div[data-name]:not(#navigable-page-hero)'))

  const getCurrentSectionIndex = (currentSections: HTMLElement[]) => {
    // Check if scrolled to the very bottom
    if (typeof window !== 'undefined') {
      const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50;
      if (isAtBottom) return currentSections.length - 1;
    }

    const index = currentSections.findIndex((section) => {
      const SECTION_DETECTION_POINT = 310
      const rect = section.getBoundingClientRect()

      return (
        rect.top <= SECTION_DETECTION_POINT &&
        rect.bottom > SECTION_DETECTION_POINT
      )
    })
    
    return index === -1 ? 0 : index;
  }

  const updateCurrentSection = () => {
    if (isClickScrolling) return
    setCurrentSectionIndex(getCurrentSectionIndex(getSections()))
  }

  const handleItemClick = (section: HTMLElement, index: number) => {
    // Set active index immediately for instant dot jump
    setCurrentSectionIndex(index)
    setIsClickScrolling(true)

    const { top } = section.getBoundingClientRect()
    const { scrollY } = window
    const scrollToSectionOffset = 300

    window.scrollTo({
      top: top + scrollY - scrollToSectionOffset,
      behavior: 'smooth', // Restore smooth scroll
    })

    // Brief delay to prevent scroll listener from overriding the manual selection
    // The delay should be long enough for the smooth scroll to finish
    setTimeout(() => {
      setIsClickScrolling(false)
    }, 1000)
  }

  useEffect(() => {
    const foundSections = getSections()
    setSections(foundSections)
    
    // Only update index on mount, don't overwrite manual selection when isClickScrolling changes
    if (!isClickScrolling) {
      setCurrentSectionIndex(getCurrentSectionIndex(foundSections))
    }

    window.addEventListener('scroll', updateCurrentSection)
    return () => {
      window.removeEventListener('scroll', updateCurrentSection)
    }
  }, [isClickScrolling])

  const getSingleMenuItem = (section: HTMLElement, index: number, isActive: boolean) => (
    <li key={section.id || index} className="list-none flex">
      <button
        onClick={() => handleItemClick(section, index)}
        type="button"
        className={`relative w-full px-6 py-3 rounded-full transition-all duration-300 font-semibold leading-[24px] lowercase text-left mb-2 truncate ${isActive
          ? 'bg-red text-white'
          : 'bg-transparent text-black hover:bg-gray-100 hover:text-black dark:text-white dark:hover:bg-white/10 dark:hover:text-white'
          }`}
      >
        {section.dataset.name}
      </button>
    </li>
  )

  const getMappedSectionsToComponents = () => {
    if (!isBrowser) {
      return <></>
    }

    return sections.map((section, index) => {
      const isActive = index === currentSectionIndex

      return getSingleMenuItem(section, index, isActive)
    })
  }

  return <div className={`${className}`}>{getMappedSectionsToComponents()}</div>
}
SideNavigation.defaultProps = {
  className: '',
}

export default SideNavigation
